/*
 * decoderPrefs.js — KNOWN decoder hardware/software preferences, learned empirically per
 * browser × codec × resolution, PLUS the shared read/write/detect helpers both the playback
 * (PlaybackPlayer) and live (PlayerWS) WebCodecs players use so they never drift.
 *
 * WHY: some browsers/GPUs hardware-decode certain (esp. low-fps surveillance) streams with a visible
 * ~2s "pumping" stutter; software decode is smooth. A player detects this at runtime from the paint
 * cadence (detectPumping) and falls back to software, persisting the choice per codec+resolution in
 * localStorage (readDecoderPref/writeDecoderPref). That runtime detection costs ~10-15s of stutter on
 * the FIRST play of a new combo. The KNOWN_DECODER_PREFS table SEEDS the preference for combos we've
 * already confirmed, so those skip detection and are smooth from frame 1.
 *
 * The storage key is the DECODE WORKLOAD (codec + resolution), NOT host/camera, so a learned pref stays
 * correct across camera swaps, eView members, AND across the live↔playback split (a live stream and a
 * recorded clip of the same camera share the workload, so learning it once in either fixes both).
 * Per-browser via localStorage.
 *
 * ⭐ EXTEND KNOWN_DECODER_PREFS as more NVRs / cameras / browsers are tested. Each entry:
 *   { browser, codec, pref, [width], [height], [maxWidth], [note] }
 *     browser : 'chrome' | 'safari' | 'firefox' | 'edge' | 'other'   (from detectBrowser())
 *     codec   : 'h264' | 'hevc'
 *     pref    : 'sw' (force software — hardware pumps this combo) | 'hw' (force hardware)
 *     width/height : exact match (omit = any); maxWidth : match when width <= maxWidth
 *   Rules are matched top-to-bottom; the FIRST match wins.
 */

// Coarse browser bucket from the UA string. Chrome bucket covers Chromium/Brave/Edge-Chromium siblings
// (Edge is split out first since its UA also contains "Chrome/").
export function detectBrowser() {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  if (/Edg\//.test(ua)) return 'edge';
  if (/OPR\//.test(ua)) return 'chrome';
  if (/Firefox\//.test(ua)) return 'firefox';
  if (/Chrome\//.test(ua)) return 'chrome';
  if (/Safari\//.test(ua)) return 'safari';
  return 'other';
}

// ⭐ THE KNOWN LIST — seed with confirmed combos; add more as they're tested.
export const KNOWN_DECODER_PREFS = [
  // Verified 2026-08-14 (David, O's Flagstaff eView, dev Chrome/Chromium on macOS): Chrome
  // HARDWARE-decodes low-fps (3-8 fps) H.264 surveillance clips with a ~2s pumping stutter; SOFTWARE
  // is smooth (confirmed at 640x360; software cost is negligible at these resolutions). Scoped to
  // <=1280 wide (typical sub-stream / low-res surveillance) pending validation on more cameras.
  { browser: 'chrome', codec: 'h264', maxWidth: 1280, pref: 'sw',
    note: 'low-res H.264 hardware pumping on Chrome/macOS (O\'s Flagstaff, 2026-08-14)' },

  // HEVC is hardware-only on macOS Safari/Chrome — never force software (handled in the players too).
  { browser: 'safari', codec: 'hevc', pref: 'hw', note: 'macOS HEVC hardware-only' },
  { browser: 'chrome', codec: 'hevc', pref: 'hw', note: 'macOS HEVC hardware-only' },
];

// Return 'sw' | 'hw' | null for a codec+resolution on the given (or detected) browser.
export function knownDecoderPref(codecKind, width, height, browser) {
  const b = browser || detectBrowser();
  for (const r of KNOWN_DECODER_PREFS) {
    if (r.browser && r.browser !== b) continue;
    if (r.codec && r.codec !== codecKind) continue;
    if (r.width != null && r.width !== width) continue;
    if (r.height != null && r.height !== height) continue;
    if (r.maxWidth != null && !(width <= r.maxWidth)) continue;
    return r.pref;
  }
  return null;
}


// ---- Shared read/write/detect helpers (used by BOTH PlaybackPlayer and PlayerWS) ----

// localStorage key for the learned decoder preference — keyed by the DECODE WORKLOAD (codec +
// resolution). Live and playback build the SAME key for the same workload, so a pref learned on one
// applies to the other. Defaults mirror the historical PlaybackPlayer key so pre-existing entries
// still resolve.
export function decoderPrefKey(codecKind, width, height) {
  return `dvlite_dec_${codecKind || 'h264'}_${width || 0}x${height || 0}`;
}

// Read the stored preference → { accel, known }.
//   known=true  → we've learned (localStorage) or seeded (KNOWN_DECODER_PREFS) this workload; use accel,
//                 skip runtime detection.
//   known=false → unknown workload → default hardware (accel:true) and let the caller run detection.
// A stored localStorage value wins over the seed table (an empirically-learned override of a seed).
export function readDecoderPref(codecKind, width, height, browser) {
  try {
    const v = localStorage.getItem(decoderPrefKey(codecKind, width, height));
    if (v === 'sw') return { accel: false, known: true };
    if (v === 'hw') return { accel: true, known: true };
  } catch (_) { /* localStorage unavailable */ }
  // No stored pref → consult the seeded known-config table so confirmed combos skip the ~15s runtime
  // detection. Unknown → default hardware + detect.
  const seed = knownDecoderPref(codecKind, width, height, browser);
  if (seed === 'sw') return { accel: false, known: true };
  if (seed === 'hw') return { accel: true, known: true };
  return { accel: true, known: false };
}

// Persist the learned choice for this workload (per-browser). useSoftware=true → 'sw', else 'hw'.
export function writeDecoderPref(codecKind, width, height, useSoftware) {
  try { localStorage.setItem(decoderPrefKey(codecKind, width, height), useSoftware ? 'sw' : 'hw'); }
  catch (_) { /* localStorage unavailable */ }
}

// Decide whether the presented-frame cadence shows the hardware-decode "pumping" pattern.
//   deltas : array of ms gaps between consecutive PRESENTED frames.
//   returns true  = pumping (switch to software), false = healthy (keep hardware),
//           null  = not enough samples yet (< MIN_SAMPLES) — sample more paints, stay undecided.
// A healthy decoder paints at a steady interval; a pumping one BURSTS — several near-0 gaps then long
// stalls → high variance. Thresholds are the single source of truth for both players (they drifting
// apart is exactly what let live detection rot).
export const PUMP_MIN_SAMPLES = 20;
export function detectPumping(deltas) {
  if (!deltas || deltas.length < PUMP_MIN_SAMPLES) return null;
  const avg = deltas.reduce((a, b) => a + b, 0) / deltas.length;
  const stdDev = Math.sqrt(deltas.reduce((s, v) => s + (v - avg) * (v - avg), 0) / deltas.length);
  const veryShort = deltas.filter(x => x < 15).length;   // painted back-to-back (a burst)
  const veryLong = deltas.filter(x => x > 300).length;   // then a long stall
  return stdDev > 80 || (veryShort > 4 && veryLong > 2);
}
